/// <amd-module name="Artifacts/FeedSettings/containers/ViewsTabContainer" />

import { ViewsTab } from "Artifacts/FeedSettings/components/ViewsTab";
import {
  FeedSettingsActions,
  IFeedSettingsModuleState,
  ViewPermissionMap,
} from "Artifacts/FeedSettings/redux";
import * as Res from "Artifacts/FeedSettings/Resources";
import { IFeedSettings } from "Artifacts/FeedSettings/types";
import { GenericModalId, IGenericModalProps } from "Artifacts/Root/components/GenericModal";
import { ComponentState } from "Artifacts/Root/Constants";
import { isPublicFeed } from "Artifacts/Root/helpers/FeedHelpers";
import { isUserFeedAdmin } from "Artifacts/Root/helpers/UserHelpers";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import { getStore } from "Artifacts/Root/Redux";
import { RootActions } from "Artifacts/Root/redux/Actions";
import { ICurrentModal } from "Artifacts/Root/redux/Contracts";
import { ViewIdentifier } from "Artifacts/Root/types/AzureArtifacts";
import { IRoutedTabContainerProps } from "Artifacts/Root/types/IRoutedTabContainerProps";
import { FeedView } from "Artifacts/Root/web-apis/contracts/Feed";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import * as Utils_String from "VSS/Core/Util/String";
import { useComponentContext } from "VSS/Platform/Layout";

type IViewsTabContainerProps = IRoutedTabContainerProps & IViewsTabMapStateToProps & typeof Actions;

const Actions = {
  loadViewsPermissions: FeedSettingsActions.loadViewsPermissions,
  updateViewPermissions: FeedSettingsActions.addOrEditView,
  makeViewDefault: FeedSettingsActions.makeViewDefault,
  deleteView: FeedSettingsActions.deleteView,
  fakeAddOrEditView: RootActions.fakeAddOrEditView,
  fakeDeleteView: RootActions.fakeDeleteView,
  showModal: RootActions.showModal,
  hideModal: RootActions.hideModal,
};

const DeleteViewModalName = "DeleteViewModal";

interface IViewsTabMapStateToProps {
  componentState: ComponentState;
  settings?: IFeedSettings;

  viewPermissionComponentState: ComponentState;
  viewsPermissionMap?: ViewPermissionMap;
  updateViewPermissionsComponentState: ComponentState;
  deleteFeedViewComponentState: ComponentState;
}

function mapStateToProps(state: IFeedSettingsModuleState): IViewsTabMapStateToProps {
  const hubState = state["feed-settings"].hubState;
  return {
    componentState: hubState.componentState,
    settings: hubState.settings,
    viewPermissionComponentState: hubState.viewsPermissionsComponentState,
    viewsPermissionMap: hubState.viewPermissionMap,
    updateViewPermissionsComponentState: hubState.updateViewPermissionsComponentState,
    deleteFeedViewComponentState: hubState.deleteFeedViewComponentState,
  };
}

const ViewsTabContainer: React.FC<IViewsTabContainerProps> = ({
  componentState,
  settings,
  loadViewsPermissions,
  viewsPermissionMap,
  viewPermissionComponentState,
  updateViewPermissions,
  updateViewPermissionsComponentState,
  showModal,
  hideModal,
  deleteFeedViewComponentState,
  makeViewDefault,
  deleteView,
}) => {
  const { feed } = useFeed();
  const { pageContext } = useComponentContext();
  const store = getStore(pageContext);
  const dispatch = store.dispatch;
  const [currentFeedId, setCurrentFeedId] = useState<string | undefined>();

  React.useEffect(() => {
    if (feed && feed.id !== currentFeedId) {
      settings &&
        loadViewsPermissions(
          feed,
          settings?.views.map<ViewIdentifier>((view) => {
            return {
              id: view.id,
              name: view.name,
              type: view.type,
            };
          }),
        );
      setCurrentFeedId(feed.id);
    }
  }, [loadViewsPermissions, feed, settings]);

  const [showAddUpdateView, setShowAddUpdateView] = useState<boolean>(false);
  const [viewToEdit, setViewToEdit] = useState<FeedView | undefined>();

  const dismissAddUpdateViewPanel = () => {
    setViewToEdit(undefined);
    setShowAddUpdateView(false);
  };

  const showDeleteViewModal = React.useCallback(
    (viewToDelete: FeedView, setFocus: () => void) => {
      const onDeleted = () => {
        dispatch(
          RootActions.fakeDeleteView({
            id: viewToDelete.id,
            name: viewToDelete.name,
            type: viewToDelete.type,
          }),
        );
        hideModal();
      };

      const modalProps: ICurrentModal<IGenericModalProps> = {
        modalId: GenericModalId,
        props: {
          title: Utils_String.format(Res.ViewDeletionDialogContent_Title, viewToDelete.name),
          primaryButtonText: Res.Delete_Label,
          primaryButtonDanger: true,
          body: Res.ViewDeletionDialogContent_PermanentDeleteViewText,
          warningCardContent: Res.ViewDeletionDialogContent_SevereWarningText,
          onPrimaryClicked: () => feed && viewToDelete && deleteView(feed, viewToDelete, onDeleted),
          onDismiss: () => {
            hideModal();
            setFocus();
          },
          primaryButtonComponentState: deleteFeedViewComponentState,
        },
      };
      showModal(modalProps, DeleteViewModalName);
    },
    [hideModal, deleteFeedViewComponentState, showModal, feed, deleteView],
  );

  return (
    <ViewsTab
      settings={settings}
      viewPermissionComponentState={viewPermissionComponentState}
      viewsPermissionMap={viewsPermissionMap}
      componentState={componentState}
      views={settings?.views}
      setShowAddUpdateView={(value: boolean, setFocus?: () => void) => {
        setShowAddUpdateView(value);
        if (setFocus) {
          setFocus();
        }
      }}
      showAddUpdateView={showAddUpdateView}
      setViewToEdit={setViewToEdit}
      viewToEdit={viewToEdit}
      updateViewPermissions={(view, isEdit, feedPermsToSet, isDefaultEnabled) => {
        feed &&
          updateViewPermissions(
            feed,
            view,
            settings?.views || [],
            isEdit,
            isDefaultEnabled,
            viewsPermissionMap ? viewsPermissionMap[view.id] : [],
            feedPermsToSet,
            () => {
              dispatch(
                RootActions.fakeAddOrEditView({
                  id: view.id,
                  name: view.name,
                  type: view.type,
                }),
              );
              dismissAddUpdateViewPanel();
            },
          );
      }}
      updateViewPermissionsComponentState={updateViewPermissionsComponentState}
      makeViewDefault={makeViewDefault}
      deleteView={showDeleteViewModal}
      isPublicFeed={feed ? isPublicFeed(feed) : false}
      isUserAdmin={feed !== undefined && isUserFeedAdmin(feed)}
      supportServicePrincipals={settings?.supportServicePrincipals ?? false}
    />
  );
};

const ConnectedViewsTabContainer = connect<
  IViewsTabMapStateToProps,
  unknown,
  IRoutedTabContainerProps
>(
  mapStateToProps,
  Actions,
)(ViewsTabContainer);

export { ConnectedViewsTabContainer as ViewsTabContainer, IViewsTabContainerProps };
